// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — annotation descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Text + multileader annotations. Excludes REVCLOUD (drawing primitive,
// authored in `commands/drawing.ts`) and dimensions (own file).
//
// Each descriptor maps to command-type literals in
// `packages/contracts/src/editor.ts`:
//   TEXT        → `geometry/create-text`                (line 315)
//   MTEXT       → `geometry/create-mtext`               (line 328)
//   MLEADER     → `geometry/create-multileader`         (line 370)
//   MLEADEREDIT → `geometry/add-leader-to-multileader`  (line 387)
//               + `geometry/remove-leader-from-multileader` (line 402)
//               + `geometry/update-multileader-vertex`
//               + `geometry/update-multileader-text-location`
//               + `geometry/update-multileader-text-rotation`
//               + `geometry/update-multileader-dogleg`
//               + `geometry/add-multileader-vertex`
//               + `geometry/remove-multileader-vertex`
//
// MLEADEREDIT is a shell-style command — at the `mleaderedit-options`
// step it branches into the Add or Remove flow, dispatching one of two
// "structural" reducer commands. The six additional literals above are
// dispatched programmatically from grip-edit handlers (drag a leader
// vertex, drag the text-content frame, rotate the text, edit the dogleg
// horizontal landing length, add a mid-leader vertex via the grip
// context menu, or remove a single mid-leader vertex via the grip
// context menu). All eight literals
// share the MLEADEREDIT semantic group
// — selecting a MULTILEADER and mutating its leaderRoots[]/textOptions
// graph — so they are listed on the one descriptor to satisfy coverage.
//
// Cross-checked against draftingHelpers.ts (alias switch + initial-step
// map) and i18n/messages.ts (RU+EN labels and prompt keys).
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_FILE = "apps/web/src/app/state/geometryReducer.ts";

export const ANNOTATION_DESCRIPTORS: readonly CommandDescriptor[] = [
  {
    id: "TEXT",
    category: "annotation",
    status: "implemented",
    aiCallable: true,
    aliases: {
      // D5 — App.tsx's shellKeyboardCommandAliases table maps bare "T" to
      // the TEXT draft flow, but real AutoCAD's "T" alias belongs to
      // MTEXT (see that descriptor's own comment) and the registry already
      // claims it there. Adding "T" here too would collide (C.ALIAS).
      // The app-specific T→TEXT shell-keyboard shortcut is preserved as a
      // LOCAL override in useCommandLine.ts's derivation (documented
      // there) rather than a registry alias, so it does not misrepresent
      // AutoCAD's real single-letter convention to AI / docs consumers.
      en: ["TEXT", "DT", "DTEXT"],
      ru: ["ТЕКСТ"],
    },
    commandTypeLiterals: ["geometry/create-text"],
    draftCommandId: "TEXT",
    draftSteps: [
      {
        stepId: "text-insertion-point",
        promptKeyRu: "prompt.text.start",
        promptKeyEn: "prompt.text.start",
        expects: "point",
        // AutoCAD's TEXT prompt offers Justify and Style sub-keywords;
        // they advance into separate sub-prompts handled outside this
        // descriptor's main step list.
        keywords: ["J", "S"],
      },
      {
        stepId: "text-height",
        promptKeyRu: "prompt.text.height",
        promptKeyEn: "prompt.text.height",
        expects: "distance",
      },
      {
        stepId: "text-rotation",
        promptKeyRu: "prompt.text.rotation",
        promptKeyEn: "prompt.text.rotation",
        expects: "angle",
      },
      {
        stepId: "text-content",
        promptKeyRu: "prompt.text.content",
        promptKeyEn: "prompt.text.content",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "spaceId",
        type: "string",
        required: true,
        description: {
          ru: "Идентификатор пространства (Model или Layout).",
          en: "Identifier of the target space (Model or Layout).",
        },
      },
      {
        name: "insertionPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка вставки текста — определяется выравниванием базовой линии.",
          en: "Text insertion point — anchored to the baseline by the current justification.",
        },
      },
      {
        name: "height",
        type: "number",
        required: true,
        description: {
          ru: "Высота текста в единицах модели (положительное число).",
          en: "Text height in drawing units (positive number).",
        },
      },
      {
        name: "contents",
        type: "string",
        required: true,
        description: {
          ru: "Содержимое строки текста.",
          en: "The text string itself.",
        },
      },
      {
        name: "rotation",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Угол поворота в радианах относительно базовой линии.",
          en: "Rotation angle in radians, measured against the baseline.",
        },
      },
      {
        name: "styleName",
        type: "string",
        required: false,
        defaultValue: "Standard",
        description: {
          ru: "Имя текстового стиля (TEXTSTYLE) из таблицы STYLE.",
          en: "Text-style name from the STYLE table (TEXTSTYLE).",
        },
      },
    ],
    help: {
      ru: {
        short:
          "Однострочный текст (TEXT/DTEXT) — базовая аннотация в одну строку с фиксированным стилем.",
        long: "TEXT (DTEXT, ТЕКСТ) создаёт однострочный текстовый объект DXF entity TEXT. После указания точки вставки запрашивается высота, угол поворота и содержимое. В отличие от MTEXT не поддерживает форматирование внутри одной надписи; для абзацев и форматирования используйте MTEXT. Текст наследует текущий слой и стиль (TEXTSTYLE).",
        examples: ["TEXT", "DT", "DTEXT", "ТЕКСТ"],
        seeAlso: ["MTEXT", "MLEADER", "ATTDEF"],
      },
      en: {
        short:
          "Single-line text (TEXT/DTEXT) — basic one-line annotation with a single style.",
        long: "TEXT (DTEXT) creates a DXF TEXT entity. After picking the insertion point the user is asked for height, rotation and content. Unlike MTEXT it does not support inline formatting; use MTEXT for paragraphs and rich text. The text inherits the current layer and TEXTSTYLE.",
        examples: ["TEXT", "DT", "DTEXT"],
        seeAlso: ["MTEXT", "MLEADER", "ATTDEF"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.text.label",
      labelKeyEn: "command.text.label",
      ribbonTab: "annotate",
      ribbonSection: "text",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/create-text", file: REDUCER_FILE },
    ],
  },
  {
    id: "MTEXT",
    category: "annotation",
    status: "implemented",
    aiCallable: true,
    aliases: {
      // T is documented in AutoCAD as a one-letter alias for MTEXT but in
      // our current draftingHelpers it is not wired (the switch only
      // accepts MTEXT|MT). We keep T in the EN aliases so AI / docs are
      // aware of it; the alias-parity validator will flag the gap and the
      // follow-up PR can extend the switch.
      en: ["MTEXT", "MT", "T"],
      ru: ["МТЕКСТ", "МТ"],
    },
    commandTypeLiterals: ["geometry/create-mtext"],
    draftCommandId: "MTEXT",
    draftSteps: [
      {
        stepId: "mtext-first-corner",
        promptKeyRu: "prompt.mtext.first",
        promptKeyEn: "prompt.mtext.first",
        expects: "point",
      },
      {
        stepId: "mtext-second-corner",
        promptKeyRu: "prompt.mtext.second",
        promptKeyEn: "prompt.mtext.second",
        expects: "point",
      },
      {
        stepId: "mtext-content",
        promptKeyRu: "prompt.mtext.content",
        promptKeyEn: "prompt.mtext.content",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "spaceId",
        type: "string",
        required: true,
        description: {
          ru: "Идентификатор пространства (Model или Layout).",
          en: "Identifier of the target space (Model or Layout).",
        },
      },
      {
        name: "insertionPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка вставки — верхний левый угол первой строки текстового поля.",
          en: "Insertion point — top-left corner of the first text-box line.",
        },
      },
      {
        name: "nominalHeight",
        type: "number",
        required: true,
        description: {
          ru: "Номинальная высота символов в единицах модели (positive).",
          en: "Nominal character height in drawing units (positive).",
        },
      },
      {
        name: "referenceWidth",
        type: "number",
        required: true,
        description: {
          ru: "Ширина текстового бокса для переноса строк (positive).",
          en: "Reference width of the text box for line wrapping (positive).",
        },
      },
      {
        name: "contents",
        type: "string",
        required: true,
        description: {
          ru: "Содержимое многострочного текста с инлайн-форматированием (\\f, \\C, \\H, \\P).",
          en: "Multi-line text content with inline formatting codes (\\f, \\C, \\H, \\P).",
        },
      },
      {
        name: "styleName",
        type: "string",
        required: false,
        defaultValue: "Standard",
        description: {
          ru: "Имя текстового стиля (TEXTSTYLE) из таблицы STYLE.",
          en: "Text-style name from the STYLE table (TEXTSTYLE).",
        },
      },
    ],
    help: {
      ru: {
        short:
          "Многострочный текст (MTEXT) с инлайн-форматированием и автопереносом по ширине.",
        long: "MTEXT (МТЕКСТ) создаёт многострочный текстовый объект DXF entity MTEXT. Задаётся двумя углами текстового прямоугольника, ширина определяет автоперенос. Поддерживает форматирование с inline-кодами: \\f шрифт, \\C цвет, \\H высота, \\P абзац, \\L подчёркивание. Для одиночных строк используйте TEXT.",
        examples: ["MTEXT", "MT", "МТЕКСТ"],
        seeAlso: ["TEXT", "MLEADER", "DIMENSION"],
      },
      en: {
        short:
          "Multi-line text (MTEXT) with inline formatting and word-wrap by box width.",
        long: "MTEXT creates a DXF MTEXT entity. Two corners define the text box, the width controlling word-wrap. Supports inline formatting codes: \\f font, \\C colour, \\H height, \\P paragraph, \\L underline. Use TEXT for plain single-line strings.",
        examples: ["MTEXT", "MT"],
        seeAlso: ["TEXT", "MLEADER", "DIMENSION"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.mtext.label",
      labelKeyEn: "command.mtext.label",
      ribbonTab: "annotate",
      ribbonSection: "text",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/create-mtext", file: REDUCER_FILE },
    ],
  },
  {
    id: "MLEADER",
    category: "annotation",
    status: "implemented",
    aiCallable: true,
    aliases: {
      // Real switch in draftingHelpers accepts MLEADER|ML; spec lists MLD
      // as the AutoCAD shortcut. We expose both — MLD is harmless even if
      // the switch presently only binds ML, the alias-parity validator
      // surfaces the gap as a follow-up.
      en: ["MLEADER", "ML", "MLD"],
      ru: ["МВЫНОСКА", "МЛИНИЯ"],
    },
    commandTypeLiterals: ["geometry/create-multileader"],
    draftCommandId: "MLEADER",
    draftSteps: [
      {
        stepId: "mleader-arrow-point",
        promptKeyRu: "prompt.mleader.arrow",
        promptKeyEn: "prompt.mleader.arrow",
        expects: "point",
      },
      {
        stepId: "mleader-landing-point",
        promptKeyRu: "prompt.mleader.landing",
        promptKeyEn: "prompt.mleader.landing",
        expects: "point",
      },
      {
        stepId: "mleader-content",
        promptKeyRu: "prompt.mleader.content",
        promptKeyEn: "prompt.mleader.content",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "spaceId",
        type: "string",
        required: true,
        description: {
          ru: "Идентификатор пространства (Model или Layout).",
          en: "Identifier of the target space (Model or Layout).",
        },
      },
      {
        name: "arrowPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка стрелки — наконечник выноски, привязанный к аннотируемому объекту.",
          en: "Arrowhead point — leader tip anchored to the annotated feature.",
        },
      },
      {
        name: "landingPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка посадки — место соединения горизонтального хвоста с текстом.",
          en: "Landing point — where the horizontal tail meets the text content.",
        },
      },
      {
        name: "textContents",
        type: "string",
        required: true,
        description: {
          ru: "Текстовое содержимое выноски (поддерживает MTEXT-форматирование).",
          en: "Text content of the leader callout (supports MTEXT formatting).",
        },
      },
    ],
    help: {
      ru: {
        short:
          "Мультивыноска (MLEADER) — выноска со стрелкой, посадочной линией и текстом.",
        long: "MLEADER (МВЫНОСКА) создаёт мультивыноску DXF entity MULTILEADER. Запрашивает точку стрелки, точку посадки и содержимое текста. Объект поддерживает несколько лидеров на одну выноску (см. MLEADEREDIT для добавления/удаления стрелок), произвольное число изломов на каждом лидере, а также пользовательский MLEADERSTYLE через стиль (полная поддержка leaderRoots[], textBoxOptions, scale, dogleg см. в editor.ts:370-468). Алиас MLD — стандартный AutoCAD.",
        examples: ["MLEADER", "ML", "МВЫНОСКА"],
        seeAlso: ["MLEADEREDIT", "MTEXT", "TEXT"],
      },
      en: {
        short:
          "Multileader (MLEADER) — annotation leader with arrow, landing line and text.",
        long: "MLEADER creates a DXF MULTILEADER entity. The user picks the arrow point, the landing point and the text content. The entity supports multiple leaders per callout (see MLEADEREDIT for add/remove), arbitrary vertex counts per leader, and a user-defined MLEADERSTYLE (full support for leaderRoots[], textBoxOptions, scale, dogleg — see editor.ts:370-468). MLD is the standard AutoCAD shortcut.",
        examples: ["MLEADER", "ML", "MLD"],
        seeAlso: ["MLEADEREDIT", "MTEXT", "TEXT"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.mleader.label",
      labelKeyEn: "command.mleader.label",
      ribbonTab: "annotate",
      ribbonSection: "leaders",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-multileader",
        file: REDUCER_FILE,
      },
    ],
  },
  {
    id: "MLEADEREDIT",
    category: "annotation",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 + no shipped ai-commands action (multileader
    // dispatcher absent — see multileaderDispatcher.test.ts GAP); AI surface deferred
    // to CC-2c + explicit ТЗ §17 revision.
    aiCallable: false,
    aliases: {
      en: ["MLEADEREDIT", "MLE"],
      ru: ["МВЫНОСКАРЕД", "МЛИНИЯРЕД"],
    },
    // MLEADEREDIT branches into add or remove via the keyword step,
    // and additionally owns six grip-edit literals dispatched from
    // the on-canvas handlers (vertex drag, text-frame drag, text-frame
    // rotate, dogleg drag, and the grip right-click context-menu
    // Add Vertex / Remove Vertex items — see AppGripMenus.tsx). All
    // eight literals share the MLEADEREDIT
    // semantic group — selecting a MULTILEADER and mutating its
    // leaderRoots[]/textOptions graph.
    commandTypeLiterals: [
      "geometry/add-leader-to-multileader",
      "geometry/remove-leader-from-multileader",
      "geometry/update-multileader-vertex",
      "geometry/update-multileader-text-location",
      "geometry/update-multileader-text-rotation",
      "geometry/update-multileader-dogleg",
      "geometry/add-multileader-vertex",
      "geometry/remove-multileader-vertex",
    ],
    draftCommandId: "MLEADEREDIT",
    draftSteps: [
      {
        stepId: "mleaderedit-select",
        promptKeyRu: "prompt.mleaderedit.select",
        promptKeyEn: "prompt.mleaderedit.select",
        expects: "selection",
      },
      {
        stepId: "mleaderedit-options",
        promptKeyRu: "prompt.mleaderedit.options",
        promptKeyEn: "prompt.mleaderedit.options",
        expects: "keyword",
        keywords: ["A", "R"],
      },
      {
        stepId: "mleaderedit-add-leader-arrow",
        promptKeyRu: "prompt.mleaderedit.add_arrow",
        promptKeyEn: "prompt.mleaderedit.add_arrow",
        expects: "point",
      },
      {
        stepId: "mleaderedit-remove-pick",
        promptKeyRu: "prompt.mleaderedit.remove_pick",
        promptKeyEn: "prompt.mleaderedit.remove_pick",
        expects: "selection",
      },
    ],
    parameters: [
      {
        name: "entityId",
        type: "entityId",
        required: true,
        description: {
          ru: "Идентификатор существующего объекта MULTILEADER.",
          en: "Identifier of the target MULTILEADER entity.",
        },
      },
      {
        name: "arrowPoint",
        type: "point2d",
        required: false,
        description: {
          ru: "Точка стрелки нового лидера (только для режима Add Leader).",
          en: "Arrow point of the new leader line (Add Leader mode only).",
        },
      },
      {
        name: "leaderIndex",
        type: "number",
        required: false,
        description: {
          ru: "Индекс удаляемого лидера (только для режима Remove Leader).",
          en: "Index of the leader to remove (Remove Leader mode only).",
        },
      },
      {
        name: "confirmed",
        type: "boolean",
        required: false,
        description: {
          ru: "Подтверждение удаления последнего лидера (удалит весь объект MULTILEADER).",
          en: "Confirms removal of the last leader (will delete the whole MULTILEADER).",
        },
      },
      {
        name: "leaderRootIndex",
        type: "number",
        required: false,
        description: {
          ru: "Индекс лидера в leaderRoots[] (контрактное поле rootIndex) для grip-edit литералов (update/remove/add-multileader-vertex, dogleg).",
          en: "Index of the leader in leaderRoots[] (the contract's rootIndex) for grip-edit literals (update/remove/add-multileader-vertex, dogleg).",
        },
      },
      {
        name: "nodeIndex",
        type: "number",
        required: false,
        description: {
          ru: "Индекс узла внутри лидера (контрактное поле nodeIndex) для update/remove/add-multileader-vertex и dogleg.",
          en: "Index of the node within the leader root (the contract's nodeIndex) for update/remove/add-multileader-vertex and dogleg.",
        },
      },
      {
        name: "lineIndex",
        type: "number",
        required: false,
        description: {
          ru: "Индекс линии лидера внутри узла (контрактное поле lineIndex) для update/remove/add-multileader-vertex.",
          en: "Index of the leader line within the node (the contract's lineIndex) for update/remove/add-multileader-vertex.",
        },
      },
      {
        name: "vertexIndex",
        type: "number",
        required: false,
        description: {
          ru: "Индекс вершины внутри линии лидера (для update/remove-multileader-vertex; для add-multileader-vertex — вершина, ПОСЛЕ которой вставляется новая).",
          en: "Vertex index within the leader line (for update/remove-multileader-vertex; for add-multileader-vertex it is the vertex AFTER which the new one is inserted).",
        },
      },
      {
        name: "vertexPosition",
        type: "point2d",
        required: false,
        description: {
          ru: "Новое положение вершины (контрактное поле newPosition) — для update-multileader-vertex (после grip-drag) и add-multileader-vertex (позиция вставляемой вершины).",
          en: "New vertex position (the contract's newPosition) — for update-multileader-vertex (after grip-drag) and add-multileader-vertex (the inserted vertex position).",
        },
      },
      {
        name: "textLocation",
        type: "point2d",
        required: false,
        description: {
          ru: "Новое положение фрейма текста после drag (для update-multileader-text-location).",
          en: "New text frame position after drag (for update-multileader-text-location).",
        },
      },
      {
        name: "textRotation",
        type: "number",
        required: false,
        description: {
          ru: "Новый угол поворота текста в радианах (для update-multileader-text-rotation).",
          en: "New text rotation angle in radians (for update-multileader-text-rotation).",
        },
      },
      {
        name: "doglegLength",
        type: "number",
        required: false,
        description: {
          ru: "Новая длина горизонтального пера-посадки dogleg (для update-multileader-dogleg).",
          en: "New horizontal landing dogleg length (for update-multileader-dogleg).",
        },
      },
    ],
    help: {
      ru: {
        short:
          "Редактор мультивыноски — добавление, удаление и грип-редактирование лидеров.",
        long: "MLEADEREDIT (МВЫНОСКАРЕД, МЛИНИЯРЕД) после выбора объекта MULTILEADER предлагает на выбор Add (A) или Remove (R). В режиме Add — указывается новая точка стрелки и к мультивыноске добавляется ещё один лидер (geometry/add-leader-to-multileader). В режиме Remove — выбирается одна из существующих стрелок и она удаляется (geometry/remove-leader-from-multileader); если удаляется последний лидер, весь MULTILEADER удаляется (с подтверждением). Дополнительно команда покрывает грип-редактирование на холсте: drag вершины лидера (geometry/update-multileader-vertex), drag фрейма текста (geometry/update-multileader-text-location), вращение фрейма текста (geometry/update-multileader-text-rotation), drag горизонтального пера-посадки dogleg (geometry/update-multileader-dogleg), добавление промежуточной вершины через пункт контекстного меню грипа «Добавить вершину» (geometry/add-multileader-vertex) и удаление промежуточной вершины через пункт контекстного меню грипа «Удалить вершину» (geometry/remove-multileader-vertex). Алиас MLE — стандартный AutoCAD.",
        examples: ["MLEADEREDIT", "MLE", "МВЫНОСКАРЕД"],
        seeAlso: ["MLEADER", "MTEXT"],
      },
      en: {
        short:
          "Multileader editor — add/remove leaders and grip-edit vertex/text/dogleg geometry.",
        long: "MLEADEREDIT prompts the user to pick a MULTILEADER, then offers Add (A) or Remove (R). In Add mode the user specifies a new arrow point and an additional leader line is appended (geometry/add-leader-to-multileader). In Remove mode the user picks one of the existing arrows to delete (geometry/remove-leader-from-multileader); removing the last leader deletes the entire MULTILEADER (with confirmation). The descriptor additionally owns six on-canvas grip-edit literals: dragging a leader vertex (geometry/update-multileader-vertex), dragging the text content frame (geometry/update-multileader-text-location), rotating the text frame (geometry/update-multileader-text-rotation), dragging the horizontal landing dogleg length (geometry/update-multileader-dogleg), adding a mid-leader vertex via the grip right-click context menu's 'Add Vertex' item (geometry/add-multileader-vertex) and removing a mid-leader vertex via the grip context menu's 'Remove Vertex' item (geometry/remove-multileader-vertex). MLE is the standard AutoCAD shortcut.",
        examples: ["MLEADEREDIT", "MLE"],
        seeAlso: ["MLEADER", "MTEXT"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.mleaderedit.label",
      labelKeyEn: "command.mleaderedit.label",
      ribbonTab: "annotate",
      ribbonSection: "leaders",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/add-leader-to-multileader",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/remove-leader-from-multileader",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/update-multileader-vertex",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/update-multileader-text-location",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/update-multileader-text-rotation",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/update-multileader-dogleg",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/add-multileader-vertex",
        file: REDUCER_FILE,
      },
      {
        commandTypeLiteral: "geometry/remove-multileader-vertex",
        file: REDUCER_FILE,
      },
    ],
  },
];
